import type { CollectionViewDTO } from '@next-space/fe-api-idl';
import { CollectionViewType } from '@next-space/fe-api-idl';
import { getRelationRecords } from 'src/bitable/table-view/cell/relation/get-relation-records';
import { getState } from 'src/redux/store';
import type { NextBlock, RootState } from 'src/redux/types';
import { useObservableBlock } from 'src/services/rxjs-redux/use-obs-block';
import { useObservableStore } from 'src/services/rxjs-redux/use-obs-store';
import { useGetSortedRecordIds } from '../collection-view/use-get-sorted-records';
import { useBitable } from 'src/bitable/context';

/** TODO */
export const useOpenSubItem = (collectionId: string | undefined) => {
  return useObservableBlock(collectionId, (_) => {
    return isOpenSubitem(collectionId);
  });
};
export const useSubitemSetting = (collectionId: string | undefined) => {
  return useObservableBlock(collectionId, (b) => {
    return b?.data.format?.openSubItem;
  });
};

export const isOpenSubitem = (collectionId: string | undefined) => {
  if (!collectionId) return;
  const block = getState().blocks[collectionId];
  const openSubItem = block?.data.format?.openSubItem;
  if (!openSubItem) return false;
  if (!openSubItem.childPropertyId || !openSubItem.parentPropertyId) return false;
  return Boolean(
    block?.data.schema?.[openSubItem.childPropertyId] &&
      block?.data.schema[openSubItem.parentPropertyId]
  );
};
export const getSubitemTree = (collection: NextBlock | undefined, sortIds?: string[]) => {
  if (!collection) return;
  const openSubitem = isOpenSubitem(collection.uuid);
  if (openSubitem) {
    const subNodes = sortIds ?? collection.subNodes;
    // 打开了subitem功能的重新按层级排序
    const subitem = collection.data.format?.openSubItem;
    if (subitem && subitem.childPropertyId && subitem.parentPropertyId) {
      const parentRelationMap = new Map<string, Set<string>>();
      const childRelationMap = new Map<string, Set<string>>();
      subNodes.forEach((blockId) => {
        const { relationRecords: childRelationRecords } = getRelationRecords(
          blockId,
          subitem.childPropertyId!
        );
        const { relationRecords: parentRelationRecords } = getRelationRecords(
          blockId,
          subitem.parentPropertyId!
        );
        const childSet = childRelationMap.get(blockId) ?? new Set();
        childRelationRecords.forEach((recordId) => {
          childSet.add(recordId);
        });
        childRelationMap.set(blockId, childSet);
        const parentSet = parentRelationMap.get(blockId) ?? new Set();
        parentRelationRecords.forEach((recordId) => {
          parentSet.add(recordId);
        });
        parentRelationMap.set(blockId, parentSet);
      });
      // 重新排序
      const sortChildRelationMap = new Map<string, Set<string>>();
      subNodes.forEach((subId) => {
        childRelationMap.forEach((v, k) => {
          const set = sortChildRelationMap.get(k) ?? new Set();
          if (v.has(subId)) {
            set.add(subId);
          }
          sortChildRelationMap.set(k, set);
        });
      });
      return [sortChildRelationMap, parentRelationMap];
    }
  }
};

export const getAllRecordIds = (collectionId: string, ids: string[]) => {
  const subitemTree = getSubitemTree(getState().blocks[collectionId]);
  let allIds: string[] = ids;
  const subitemMap = subitemTree?.[0];
  if (subitemMap) {
    ids.forEach((v) => {
      const childIds = getSubitemChildIds(v, subitemMap);
      allIds.push(v);
      allIds.push(...childIds);
    });
    // 去重
    allIds = [...new Set(allIds)];
  }
  return allIds;
};

export const useSubitemTree = () => {
  const { collectionId, viewId } = useBitable();
  const sortIds = useGetSortedRecordIds(viewId, undefined, false);
  return getSubitemTree(getState().blocks[collectionId], sortIds);
  // 下面这段代码死活不刷新数据，没招了就按上面的来吧
  // return useObservableBlock(
  //   collectionId,
  //   (b) => {
  //     console.log('sortIds2: ', sortIds);
  //     return getSubitemTree(b, sortIds);
  //   },
  //   [sortIds],
  //   {
  //     // 不知道为啥拖拽子任务顺序会不更新，useRedux这个字段用了也不行
  //     useRedux: true,
  //   }
  // );
};

export const getSubitemChildIds = (
  parentId: string,
  childIdMap: Map<string, Set<string>>,
  deep = 0
) => {
  const set = childIdMap.get(parentId);
  const ids: string[] = [];
  if (deep >= 15) return [];
  if (set) {
    set.forEach((v) => {
      ids.push(v);
      const childIds = getSubitemChildIds(v, childIdMap, deep + 1);
      ids.push(...childIds);
    });
    return ids;
  }
  return [];
};

export const useSubitemStyle = (viewId: string) => {
  return useObservableStore(
    (s) => {
      return getSubitemStyle(viewId, s);
    },
    [viewId]
  );
};
export const getSubitemStyle = (viewId: string, s: RootState) => {
  const view = s.collectionViews[viewId];
  let defaultStyle: CollectionViewDTO['format']['subItemstyle'] = 'Card';

  if (isNestedStyleViewType(view?.type)) {
    defaultStyle = 'Nested';
  }
  return view?.format.subItemstyle ?? defaultStyle;
};

export const isNestedStyleViewType = (type?: CollectionViewType) => {
  // 表格、目录、时间轴视图 为逐层嵌套
  return (
    type &&
    [CollectionViewType.TABLE, CollectionViewType.TIMELINE, CollectionViewType.LIST].includes(type)
  );
};
